import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  padding: 40px 0;
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  color: white;
  border-radius: 12px;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin: 0 0 15px 0;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 0;
  opacity: 0.9;
`;

const ContentSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 1.8rem;
  margin: 0 0 20px 0;
  border-bottom: 3px solid #2d3748;
  padding-bottom: 10px;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin: 15px 0;
  padding: 15px;
  background: #f7fafc;
  border-radius: 8px;
  border-left: 4px solid #2d3748;
`;

const BenefitIcon = styled.span`
  font-size: 1.5rem;
  margin-right: 15px;
  color: #2d3748;
`;

const WarningBox = styled.div`
  background: #fed7d7;
  border: 2px solid #fc8181;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
`;

const WarningTitle = styled.h4`
  color: #c53030;
  margin: 0 0 15px 0;
  font-size: 1.2rem;
`;

const WarningText = styled.p`
  color: #c53030;
  margin: 10px 0;
  line-height: 1.5;
`;

const ProtectionBox = styled.div`
  background: #c6f6d5;
  border: 2px solid #68d391;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
`;

const ProtectionTitle = styled.h4`
  color: #22543d;
  margin: 0 0 15px 0;
  font-size: 1.2rem;
`;

const ProtectionText = styled.p`
  color: #22543d;
  margin: 10px 0;
  line-height: 1.5;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  color: white;
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  margin: 30px 0;
`;

const CTATitle = styled.h3`
  font-size: 1.8rem;
  margin: 0 0 15px 0;
`;

const CTAText = styled.p`
  font-size: 1.1rem;
  margin: 0 0 25px 0;
  opacity: 0.9;
`;

const OwnerProtection = () => {
  return (
    <>
      <Helmet>
        <title>ShowingBooker: Owner Protection - Landlord Liability & Compliance</title>
        <meta name="description" content="Protect yourself from liability with LA Housing Department compliance. Professional showing documentation and tenant notice management for property owners." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://showingbooker.com/owner-protection" />
        <meta property="og:title" content="ShowingBooker: Owner Protection" />
        <meta property="og:description" content="Protect yourself from liability with LA Housing Department compliance. Professional showing documentation and tenant notice management for property owners." />
        <meta property="og:image" content="https://showingbooker.com/ShowingBooker - Logo - Social Media Card.png" />
        <meta property="og:site_name" content="ShowingBooker" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://showingbooker.com/owner-protection" />
        <meta property="twitter:title" content="ShowingBooker: Owner Protection" />
        <meta property="twitter:description" content="Protect yourself from liability with LA Housing Department compliance. Professional showing documentation and tenant notice management for property owners." />
        <meta property="twitter:image" content="https://showingbooker.com/ShowingBooker - Logo - Social Media Card.png" />
      </Helmet>
      
      <Container>
        <Header>
          <Title>Owner Protection & Compliance</Title>
          <Subtitle>Protect your investment from city agencies and tenant disputes</Subtitle>
        </Header>

        <ContentSection>
          <SectionTitle>🛡️ Why Property Owners Need ShowingBooker</SectionTitle>
          <BenefitsList>
            <BenefitItem>
              <BenefitIcon>🏛️</BenefitIcon>
              <div>
                <strong>LA Housing Department Protection:</strong> Automated compliance with LA Municipal Code requirements prevents costly violations and fines from city agencies
              </div>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>📋</BenefitIcon>
              <div>
                <strong>Legal Documentation:</strong> Complete paper trail of all notices, communications, and tenant interactions protects you in disputes
              </div>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>⚖️</BenefitIcon>
              <div>
                <strong>Liability Reduction:</strong> Professional third-party notice management reduces your direct exposure to tenant complaints
              </div>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>💰</BenefitIcon>
              <div>
                <strong>Cost Protection:</strong> Avoid expensive legal fees, city fines, and tenant lawsuit settlements through proper compliance
              </div>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>🔒</BenefitIcon>
              <div>
                <strong>Professional Distance:</strong> Third-party platform handles tenant communications, reducing direct confrontation and complaints
              </div>
            </BenefitItem>
          </BenefitsList>
        </ContentSection>

        <ContentSection>
          <SectionTitle>⚠️ City Agency Compliance Risks</SectionTitle>
          <WarningBox>
            <WarningTitle>LA Housing Department Violations You Could Face:</WarningTitle>
            <WarningText>
              <strong>• Inadequate Notice:</strong> Fines up to $2,000 per violation for insufficient notice to tenants
            </WarningText>
            <WarningText>
              <strong>• Harassment Claims:</strong> Multiple complaints can trigger LAHD investigations and enforcement actions
            </WarningText>
            <WarningText>
              <strong>• Rent Stabilization Violations:</strong> RSO properties face additional scrutiny and potential penalties
            </WarningText>
            <WarningText>
              <strong>• Pattern of Abuse:</strong> Multiple tenant complaints can result in systemic violations and property restrictions
            </WarningText>
            <WarningText>
              <strong>• Legal Costs:</strong> Defending against city actions and tenant lawsuits can cost $10,000-$50,000+ per case
            </WarningText>
          </WarningBox>
        </ContentSection>

        <ContentSection>
          <SectionTitle>✅ ShowingBooker Protection Benefits</SectionTitle>
          <ProtectionBox>
            <ProtectionTitle>How ShowingBooker Protects Property Owners:</ProtectionTitle>
            <ProtectionText>
              <strong>• Automated Legal Compliance:</strong> Every showing generates CA Civil Code Section 1954 compliant notices automatically
            </ProtectionText>
            <ProtectionText>
              <strong>• Multi-Channel Documentation:</strong> Email, SMS, and certified mail create bulletproof evidence of proper notice
            </ProtectionText>
            <ProtectionText>
              <strong>• Third-Party Buffer:</strong> ShowingBooker handles tenant communications, reducing direct owner-tenant friction
            </ProtectionText>
            <ProtectionText>
              <strong>• Professional Standards:</strong> $15 fee ensures only serious, qualified buyers schedule showings
            </ProtectionText>
            <ProtectionText>
              <strong>• Compliance Archives:</strong> Complete records available for city inspections or legal proceedings
            </ProtectionText>
            <ProtectionText>
              <strong>• Tenant Satisfaction:</strong> Professional process reduces complaints and improves tenant relations
            </ProtectionText>
          </ProtectionBox>
        </ContentSection>

        <ContentSection>
          <SectionTitle>📊 Cost-Benefit Analysis</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
            <div style={{ background: '#fed7d7', padding: '20px', borderRadius: '8px', border: '2px solid #fc8181' }}>
              <h4 style={{ color: '#c53030', margin: '0 0 15px 0' }}>WITHOUT ShowingBooker:</h4>
              <ul style={{ color: '#c53030', margin: '0', paddingLeft: '20px' }}>
                <li>City fines: $2,000+ per violation</li>
                <li>Legal defense: $10,000-$50,000+</li>
                <li>Tenant lawsuit settlements: $5,000-$25,000+</li>
                <li>Property management headaches</li>
                <li>Direct tenant confrontations</li>
                <li>No documentation defense</li>
              </ul>
            </div>
            <div style={{ background: '#c6f6d5', padding: '20px', borderRadius: '8px', border: '2px solid #68d391' }}>
              <h4 style={{ color: '#22543d', margin: '0 0 15px 0' }}>WITH ShowingBooker:</h4>
              <ul style={{ color: '#22543d', margin: '0', paddingLeft: '20px' }}>
                <li>FREE for property owners</li>
                <li>Automated compliance protection</li>
                <li>Professional documentation</li>
                <li>Reduced liability exposure</li>
                <li>Third-party buffer protection</li>
                <li>Peace of mind</li>
              </ul>
            </div>
          </div>
        </ContentSection>

        <ContentSection>
          <SectionTitle>🏠 Perfect for Los Angeles Property Owners</SectionTitle>
          <div style={{ lineHeight: '1.6' }}>
            <p><strong>Rent Stabilized Properties:</strong> Extra protection from RSO compliance issues and tenant harassment claims</p>
            <p><strong>Multi-Unit Buildings:</strong> Systematic approach prevents pattern of violation accusations</p>
            <p><strong>Investment Properties:</strong> Professional management reduces hands-on liability exposure</p>
            <p><strong>Sale Preparation:</strong> Clean compliance record increases property value and reduces buyer concerns</p>
            <p><strong>Distant Owners:</strong> Automated system works without your daily involvement or oversight</p>
          </div>
        </ContentSection>

        <CTASection>
          <CTATitle>Protect Your Investment Today</CTATitle>
          <CTAText>
            Don't wait for a city violation or tenant lawsuit to discover you need better documentation. 
            ShowingBooker provides FREE liability protection for property owners.
          </CTAText>
          <CTAText>
            <strong>Zero cost to you. Maximum protection for your investment.</strong>
          </CTAText>
          <p style={{ marginTop: '30px', fontSize: '1.1rem', fontWeight: '600' }}>
            🌐 Start protecting your property at showingbooker.com
          </p>
        </CTASection>
      </Container>
    </>
  );
};

export default OwnerProtection;